import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

/** The main applicant's name, empty when the candidacy carries no contact. */
export const getCandidateName = (application: IApplication) => {
  const contact = application.contacts?.[0];

  return contact ? getName(contact) : "";
};

/**
 * The visitors who have no booked time (DEV-3407): invited by phone, or simply
 * told they may drop by. Without this list they appear nowhere in visit mode,
 * so nobody can mark them as a no-show or write down how the visit went.
 *
 * Only the "visitors" step, not the whole visitors tab: the columns before it
 * (waiting list, to plan, invited) hold people who were never let in, and a
 * doorstep list padded with them is a list nobody works through. An archived
 * candidacy stays out, as it does on the day tabs; the list endpoint already
 * leaves inactive ones out, and the check here keeps that true if a caller
 * ever hands in a list that does not. `visits` are expected without the
 * canceled ones. A cancel does not bring the candidate here: api-next moves
 * them back to "invited" so they can book again, and they show up on a day
 * once they do (Jonas, 2026-10-14).
 *
 * Inviting someone moves them out of the visitors step, which would take their
 * card away the moment the invite is sent, where a card on a day tab stays and
 * says "invited". `shownIds` are the candidacies this screen already listed
 * here; those stay while they are invited, so the tab behaves like a day.
 */
interface IGetVisitorsWithoutVisitParams {
  applications: IApplication[];
  visits: { applicationId: string }[];
  shownIds?: ReadonlySet<string>;
}

export const getVisitorsWithoutVisit = ({
  applications,
  visits,
  shownIds = new Set(),
}: IGetVisitorsWithoutVisitParams) => {
  const withVisit = new Set(visits.map(visit => visit.applicationId));

  return applications.filter(
    application =>
      (application.step === EIntakeStep.visitor ||
        (shownIds.has(application.id) &&
          (application.step === EIntakeStep.candidateTenant ||
            application.step === EIntakeStep.tenant))) &&
      !application.archivedAt &&
      application.isActive !== false &&
      !withVisit.has(application.id),
  );
};

/**
 * A visit note carries two things in one text field: the labels a broker taps
 * at the door, and whatever they type themselves.
 *
 * There is no second field to put the labels in, and this note is read in
 * several other screens (the warning triangle in the candidate list, the
 * candidacy itself, other applications by the same person), which all show it
 * raw. So the two are kept apart by a line of three middle dots: not a
 * character anyone reaches for on a keyboard, and a divider rather than an
 * eyesore in the places that print the note as it stands.
 *
 * A note written before this screen existed has no divider and no opening line,
 * and is text from top to bottom, which is what it was.
 */
const NOTE_SEPARATOR = "· · ·";

/** Between two labels on one line. The spaces are part of it, so a label may
 * hold a middle dot without being split in two. */
const LABEL_JOIN = " · ";

/**
 * What every card offers, on top of whatever the broker writes themselves.
 *
 * Observations, not verdicts, and not sorted into good and bad: a label that
 * arrives under a heading reading "negative" is a judgement recorded about a
 * person, and a list we hand a broker is a list we are answerable for. These
 * say what happened and what was said. Whether that is good news is the
 * broker's to decide, in their own words, in the field underneath.
 *
 * Nothing here touches where an income comes from, how someone looks or what
 * they studied. Means is a protected ground.
 */
export const LABEL_OPTIONS = [
  // How the visit went, first: it is the thing just observed.
  "on_time",
  "late",
  "came_with_partner",
  "partner_to_visit",
  "many_questions",
  // Then what moves in with them.
  "pet_small",
  "pet_large",
  "smoker",
  // Then what they said they want.
  "long_term",
  "negotiate_price",
];

/** Opens the label line, and titles the buttons above the note. */
export const getLabelsTitle = () => getLocalizedText("visit_mode.labels.title");

export const getLabelOptionText = (option: string) =>
  getLocalizedText(`visit_mode.labels.option.${option}`);

/**
 * Labels are stored as the words on screen rather than as keys, because the
 * note they live in is read by people, in other screens, as it stands. The
 * price is that a colleague working in another language reads the words but
 * does not get the buttons lit up, and that rewording a label in Lokalise
 * leaves the notes already written carrying the old wording.
 */
const parseLabelLine = (line: string): string[] | undefined => {
  const prefix = `${getLabelsTitle()}: `;

  if (!line.startsWith(prefix)) {
    return undefined;
  }

  return line
    .slice(prefix.length)
    .split(LABEL_JOIN)
    .map(value => value.trim())
    .filter(Boolean);
};

export const parseVisitNote = (
  content?: string | null,
): { labels: string[]; text: string } => {
  const raw = content || "";
  const lines = raw.split("\n");
  const separatorAt = lines.findIndex(line => line.trim() === NOTE_SEPARATOR);

  if (separatorAt >= 0) {
    const labels = parseLabelLine(lines.slice(0, separatorAt).join("\n"));

    // A divider with something above it that is not a label line: whatever
    // that is, somebody typed it, and handing it back in the field loses less
    // than dropping it would.
    return labels
      ? { labels, text: lines.slice(separatorAt + 1).join("\n") }
      : { labels: [], text: raw };
  }

  const labels = parseLabelLine(raw);

  return labels ? { labels, text: "" } : { labels: [], text: raw };
};

export const formatVisitNote = (labels: string[], text: string) => {
  const body = text.trim();

  if (labels.length === 0) {
    return body;
  }

  const line = `${getLabelsTitle()}: ${labels.join(LABEL_JOIN)}`;

  // No divider with nothing under it: a note that is only labels reads better
  // without a line trailing off the end of it.
  return body ? [line, NOTE_SEPARATOR, body].join("\n") : line;
};

/**
 * A label the broker wrote. It goes on the line alongside the others, so
 * anything that would break that line apart is taken out: newlines, the
 * divider between two labels, and a colon, which is how the line opens.
 */
export const sanitizeLabel = (value: string) =>
  value
    .replace(/[\n\r·:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
